import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, Chip, IconButton, LinearProgress, Paper, Stack, Typography, useTheme, Tooltip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import imageCompression from 'browser-image-compression';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../utils/capitalize';
import Icon from './shared/Icon';

interface FileUploaderProps {
    onUpload: (files: File[]) => void;
    files: File[];
    multiple?: boolean;
    accept?: string;
    maxPreviewHeight?: number;
}

type PreviewItem = {
    url: string;
    name: string;
    size: number;
    type: string;
};

const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const FileUploader: React.FC<FileUploaderProps> = ({ onUpload, files, multiple = false, accept = 'image/*', maxPreviewHeight = 88 }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [dragging, setDragging] = useState(false);
    const [compressing, setCompressing] = useState(false);
    const [progressText, setProgressText] = useState<string | null>(null);
    const [previews, setPreviews] = useState<PreviewItem[]>([]);

    useEffect(() => {
        // Build previews from provided files
        const created: PreviewItem[] = files.map((f) => ({
            url: URL.createObjectURL(f),
            name: f.name,
            size: f.size,
            type: f.type,
        }));
        // Revoke old urls on cleanup
        const old = previews;
        setPreviews(created);
        return () => {
            old.forEach((p) => URL.revokeObjectURL(p.url));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    useEffect(() => {
        // Cleanup previews when unmount
        return () => previews.forEach((p) => URL.revokeObjectURL(p.url));
    }, [previews]);

    const compressImage = async (file: File): Promise<File> => {
        const options = { maxSizeMB: 1, maxWidthOrHeight: 1500, useWebWorker: true };
        try {
            return await imageCompression(file, options);
        } catch (error) {
            // If compression fails, return the original file
            // eslint-disable-next-line no-console
            console.error('Image compression failed', error);
            return file;
        }
    };

    const handleFiles = async (selectedFiles: FileList | null) => {
        if (!selectedFiles) return;
        const arr = Array.from(selectedFiles);

        // If single mode, only keep first file
        const incoming = multiple ? arr : arr.slice(0, 1);

        // Provide visual feedback during compression
        setCompressing(true);
        setProgressText(capitalizeFirstLetter(t('compressingFiles') || 'Compressing files...'));

        const compressed: File[] = [];
        for (let i = 0; i < incoming.length; i += 1) {
            const f = incoming[i];
            // Update small progress text
            setProgressText(`${capitalizeFirstLetter(t('processing') || 'Processing')} ${i + 1}/${incoming.length}: ${f.name}`);
            if (f.type.startsWith('image/')) {
                // attempt compression
                // Note: browser-image-compression does not provide progress out of the box
                // so we show simple per-file textual progress
                // eslint-disable-next-line no-await-in-loop
                const c = await compressImage(f);
                compressed.push(c);
            } else {
                compressed.push(f);
            }
        }

        setCompressing(false);
        setProgressText(null);

        // Merge with existing files if multiple, otherwise replace
        const result = multiple ? [...files, ...compressed] : compressed;
        onUpload(result);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
        // reset value so selecting same file again triggers change
        if (e.target) e.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (e?: React.DragEvent<HTMLDivElement>) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    const openFileDialog = () => inputRef.current?.click();

    const removeFile = (index: number) => {
        const newFiles = files.filter((_, i) => i !== index);
        onUpload(newFiles);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                p: 2,
                border: `1px dashed ${dragging ? theme.palette.primary.main : theme.palette.divider}`,
                backgroundColor: dragging ? theme.palette.action.hover : 'background.paper',
                maxWidth: 520,
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="region"
            aria-label={capitalizeFirstLetter(t('fileUploader') || 'File uploader')}
        >
            <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display: 'none' }} onChange={onInputChange} aria-hidden />

            <Stack direction="row" alignItems="center" spacing={2}>
                <CloudUploadIcon color="primary" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {capitalizeFirstLetter(multiple ? t('dragAndDropFilesHere') : t('dragAndDropFileHere'))}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t('or')}{' '}
                        <Button onClick={openFileDialog} size="small" variant="text" sx={{ textTransform: 'none', p: 0, ml: 0.5 }}>
                            <Typography component="span" color="primary" sx={{ textDecoration: 'underline' }}>
                                {capitalizeFirstLetter(t('clickToUpload') || 'Click to upload')}
                            </Typography>
                        </Button>
                    </Typography>
                </Box>

                <Button variant="outlined" onClick={openFileDialog} startIcon={<AttachFileIcon />}>
                    {capitalizeFirstLetter(t('choose') || 'Choose')}
                </Button>
            </Stack>

            {compressing && (
                <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                    {progressText && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {progressText}
                        </Typography>
                    )}
                </Box>
            )}

            {files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        {capitalizeFirstLetter(multiple ? t('filesUpload') : t('fileUpload'))}:
                    </Typography>

                    <Stack spacing={1}>
                        {previews.map((p, i) => (
                            <Paper
                                key={`${p.name}-${i}`}
                                variant="outlined"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1,
                                    gap: 1.5,
                                    borderRadius: 1,
                                }}
                            >
                                <Avatar variant="rounded">{!p.type.startsWith('image/') && <Icon name="insertDriveFile" />}</Avatar>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap variant="body2" sx={{ fontWeight: 600 }}>
                                        {p.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {formatBytes(p.size)} • {p.type || '—'}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconButton aria-label={`remove-${i}`} onClick={() => removeFile(i)} size="small">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                    {/*<Tooltip title={capitalizeFirstLetter(t('delete') || 'Delete')}>
                                        <IconButton aria-label={`delete-${i}`} onClick={() => removeFile(i)} color="error" size="small">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>*/}
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>

                    {/*<Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        {files.map((f, idx) => (
                            <Chip
                                key={`${f.name}-${idx}`}
                                avatar={
                                    f.type.startsWith('image/') ? (
                                        <Avatar src={URL.createObjectURL(f)} variant="rounded" sx={{ width: 24, height: 24 }} />
                                    ) : (
                                        <Avatar>
                                            <InsertPhotoIcon fontSize="small" />
                                        </Avatar>
                                    )
                                }
                                label={`${f.name} (${formatBytes(f.size)})`}
                                onDelete={() => removeFile(idx)}
                                sx={{ maxWidth: 360, textOverflow: 'ellipsis', overflow: 'hidden' }}
                            />
                        ))}
                    </Stack>*/}
                </Box>
            )}
        </Paper>
    );
};

export default FileUploader;
